import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Trophy, Medal, Award, ChevronLeft, Sparkles, ArrowRight, Globe } from "lucide-react";
import { api } from "../lib/api";

const TIER_COLOURS = {
  free: "bg-cream text-ink border-ink/30",
  explorer: "bg-teal/20 text-teal border-teal/40",
  apprentice: "bg-marigold/20 text-marigold border-marigold/40",
  creator: "bg-[#7B5EA7]/20 text-[#7B5EA7] border-[#7B5EA7]/40",
  founder: "bg-ink text-cream border-ink",
};

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(null);   // null = worldwide
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/quiz/leaderboard/countries")
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = country ? `?country=${encodeURIComponent(country)}` : "";
    api.get(`/quiz/leaderboard${q ? `${q}&limit=30` : "?limit=30"}`)
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [country]);

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 pt-28 pb-24" data-testid="leaderboard-page">
      <Link to="/library" className="inline-flex items-center gap-1 text-xs text-ink2 hover:text-ink mb-4" data-testid="leaderboard-back">
        <ChevronLeft className="w-3 h-3" /> Back to Journey
      </Link>

      <div className="mb-10">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Trophy className="w-3 h-3" /> Study-mode leaderboard</div>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-3">The Streak Wall</h1>
        <p className="text-ink2 text-base max-w-xl">Every learner here revealed all 3 answers on a Study-mode quiz — and came back the next day.  And the day after.  Where do you stand?</p>
      </div>

      {/* Country filter chips */}
      {countries.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="leaderboard-country-filter">
          <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Filter</div>
          <button
            onClick={() => setCountry(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium border-2 transition ${country === null ? "border-ink bg-ink text-cream" : "border-ink/20 text-ink2 hover:border-ink"}`}
            data-testid="leaderboard-country-all"
          >
            🌍 Worldwide
          </button>
          {countries.map((c) => (
            <button
              key={c.code}
              onClick={() => setCountry(c.code)}
              className={`px-3 py-1 rounded-full text-xs font-medium border-2 transition ${country === c.code ? "border-marigold bg-marigold/20 text-marigold" : "border-ink/20 text-ink2 hover:border-ink"}`}
              data-testid={`leaderboard-country-${c.code}`}
            >
              {c.code} <span className="opacity-60 ml-1">· {c.learners}</span>
            </button>
          ))}
        </div>
      )}

      {loading && <div className="text-ink2 text-sm">Loading the wall of fame…</div>}

      {!loading && entries.length === 0 && (
        <div className="card-soft p-8 text-center" data-testid="leaderboard-empty">
          <Sparkles className="w-8 h-8 text-marigold mx-auto mb-3" />
          <div className="font-heading text-2xl mb-2">Be the first name on this wall.</div>
          <p className="text-ink2 mb-4">Nobody has landed a Study-mode quiz yet.  Go find a chromosome or a Journey stop and reveal all 3 answers.</p>
          <Link to="/mendel/ide" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="leaderboard-cta-empty"><Sparkles className="w-4 h-4" /> Take the first quiz</Link>
        </div>
      )}

      {!loading && podium.length > 0 && (
        <div className="grid grid-cols-3 gap-3 md:gap-5 mb-10" data-testid="leaderboard-podium">
          {[podium[1], podium[0], podium[2]].map((e, viewIdx) => {
            if (!e) return <div key={viewIdx} />;
            const podiumHeights = ["h-40", "h-52", "h-32"];
            const podiumIcons = [<Medal key="s" className="w-6 h-6 text-ink2" />, <Trophy key="g" className="w-8 h-8 text-marigold" />, <Award key="b" className="w-6 h-6 text-[#C1666B]" />];
            const podiumLabels = ["2nd", "1st", "3rd"];
            const podiumColours = [
              "bg-ink/5 border-ink/30",
              "bg-marigold/10 border-marigold",
              "bg-[#C1666B]/10 border-[#C1666B]/40",
            ];
            return (
              <div
                key={e.rank}
                className={`rounded-2xl border-2 ${podiumColours[viewIdx]} ${podiumHeights[viewIdx]} p-4 flex flex-col items-center justify-end text-center shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 transition`}
                data-testid={`leaderboard-podium-${e.rank}`}
              >
                <div className="mb-2">{podiumIcons[viewIdx]}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-0.5">{podiumLabels[viewIdx]}</div>
                <div className="font-heading text-xl md:text-2xl leading-tight break-words">{e.display_name}</div>
                <div className="mt-1 inline-flex items-center gap-1 font-mono text-sm text-marigold">
                  <Flame className="w-3.5 h-3.5" /> {e.best}
                </div>
                <TierChip tier={e.tier} />
                {e.country && (
                  <div className="mt-1 text-[9px] uppercase tracking-widest text-ink2" data-testid={`leaderboard-podium-country-${e.rank}`}>
                    <Globe className="w-2.5 h-2.5 inline mr-0.5" />{e.country}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && rest.length > 0 && (
        <div className="card-soft" data-testid="leaderboard-list">
          <table className="w-full text-sm">
            <thead className="border-b-2 border-ink/10">
              <tr className="text-[10px] uppercase tracking-widest text-ink2 font-bold">
                <th className="text-left px-4 py-3">Rank</th>
                <th className="text-left px-4 py-3">Learner</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Country</th>
                <th className="text-left px-4 py-3">Tier</th>
                <th className="text-right px-4 py-3">Best</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Current</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Quizzes</th>
              </tr>
            </thead>
            <tbody>
              {rest.map((e) => (
                <tr key={e.rank} className="border-b border-ink/5 hover:bg-marigold/5 transition" data-testid={`leaderboard-row-${e.rank}`}>
                  <td className="px-4 py-3 font-mono text-ink2 text-xs">#{e.rank}</td>
                  <td className="px-4 py-3 font-heading text-base">{e.display_name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell font-mono text-xs text-ink2" data-testid={`leaderboard-row-country-${e.rank}`}>{e.country ? <><Globe className="w-3 h-3 inline mr-0.5" />{e.country}</> : "—"}</td>
                  <td className="px-4 py-3"><TierChip tier={e.tier} /></td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-marigold inline-flex items-center gap-1 justify-end w-full"><Flame className="w-3 h-3" />{e.best}</td>
                  <td className="px-4 py-3 text-right font-mono hidden sm:table-cell text-ink2">{e.stale ? <span className="italic text-ink2/60">cold</span> : e.current}</td>
                  <td className="px-4 py-3 text-right font-mono hidden md:table-cell text-ink2">{e.total_attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="mt-8 rounded-2xl border-2 border-dashed border-marigold/50 p-6 text-center bg-marigold/5" data-testid="leaderboard-cta">
          <div className="font-heading text-2xl mb-1">Not on the wall yet?</div>
          <p className="text-ink2 mb-4 text-sm">Every 3-question streak counts.  Genes, chromosomes, road-trip stops — they all bump your streak.</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/mendel/ide" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="leaderboard-cta-mendel"><Sparkles className="w-4 h-4" /> Try Mendel Study Mode</Link>
            <Link to="/library" className="btn-outline-ink text-sm inline-flex items-center gap-2" data-testid="leaderboard-cta-journey">Take a Journey stop <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      )}
    </div>
  );
}

function TierChip({ tier }) {
  const cls = TIER_COLOURS[tier] || TIER_COLOURS.free;
  return (
    <span className={`inline-block text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${cls}`} data-testid={`leaderboard-tier-${tier}`}>
      {tier}
    </span>
  );
}
